import type { AppLayoutMode } from "@/hooks/useAppLayout";
import type { Theme } from "@/hooks/useTheme";
import type { TimezoneMode } from "@/lib/dateFormat";
import {
  normalizeAnalyticsChartHeight,
  normalizeDetectionRepresentation,
  type AnalyticsVisualPreferences,
} from "@/lib/live/visualPreferences";

export const UI_PREFERENCES_SETTING = "ui_preferences";

export interface AccountPreferences {
  theme: Theme;
  layoutMode: AppLayoutMode;
  timezone: TimezoneMode;
  charts: AnalyticsVisualPreferences;
}

export type AccountPreferencesUpdate = Omit<Partial<AccountPreferences>, "charts"> & {
  charts?: Partial<AnalyticsVisualPreferences>;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function readAccountPreferences(
  settings: Record<string, unknown> | undefined,
): AccountPreferences {
  const preferences = asRecord(settings?.[UI_PREFERENCES_SETTING]);
  const charts = asRecord(preferences.charts);

  return {
    theme:
      preferences.theme === "dark" || preferences.theme === "system" ? preferences.theme : "light",
    layoutMode: preferences.layoutMode === "compact" ? "compact" : "workspace",
    timezone: preferences.timezone === "local" ? "local" : "utc",
    charts: {
      detectionRepresentation: normalizeDetectionRepresentation(charts.detectionRepresentation),
      analyticsChartHeight: normalizeAnalyticsChartHeight(charts.analyticsChartHeight),
    },
  };
}

export function updateAccountPreferences(
  settings: Record<string, unknown>,
  changes: AccountPreferencesUpdate,
): Record<string, unknown> {
  const preferences = asRecord(settings[UI_PREFERENCES_SETTING]);
  return {
    ...settings,
    [UI_PREFERENCES_SETTING]: {
      ...preferences,
      ...changes,
      ...(changes.charts ? { charts: { ...asRecord(preferences.charts), ...changes.charts } } : {}),
    },
  };
}
